/**
 * Thai phone numbers inside a customer's chat message (owner 26 ก.ย. 69: "ถ้าระบบจับได้ว่าเป็นเบอร์ให้มีปุ่มกดเชื่อม
 * ที่ข้อความได้เลย"). Runs in the browser on text the staff already sees — nothing is stored or logged.
 *
 * Accepted: mobile 06/08/09 + 8 digits · landline 0[2-7] + 7 digits · written with spaces, dashes or dots in the
 * usual Thai groupings (085-883-6069, 085 8836069, 02-123-4567, 053-123-456) · Thai digits ๐–๙ · "+66"/"66" prefix
 * (+66 85 883 6069, 66858836069). Everything is normalised to 0XXXXXXXXX.
 *
 * Refused on purpose: amounts (1,216), order codes (20260926-105), postcodes (10110), dates (09-10-2569 10:30),
 * masked numbers (•••-•••-4521), numbers glued to letters (tracking EF123456789TH, TH0858836069) and digit
 * groups that are not a phone layout.
 */

export interface FoundPhone {
  /** 0XXXXXXXXX (9 digits for a landline, 10 for a mobile) */
  digits: string
  /** 085-883-6069 · 02-123-4567 · 053-123-456 */
  display: string
  kind: 'mobile' | 'landline'
}

const THAI_DIGITS = '๐๑๒๓๔๕๖๗๘๙'
const MAX_PER_MESSAGE = 3

/** Group sizes (after the prefix) that are how people really write the number. */
const LOCAL_LAYOUTS: Record<number, string[]> = {
  10: ['10', '3,3,4', '3,7', '3,4,3', '6,4', '2,4,4'], // mobile 085-883-6069
  9: ['9', '2,3,4', '2,7', '3,3,3', '3,6', '5,4'], // landline 02-123-4567 · 053-123-456
}
/** After +66 / 66 (the leading 0 dropped): 85 883 6069 · 2 123 4567 */
const INTL_LAYOUTS: Record<number, string[]> = {
  9: ['9', '2,3,4', '1,4,4', '2,7', '3,3,3', '1,8'],
  8: ['8', '1,3,4', '1,7', '4,4', '2,3,3'],
}

export function thaiToAscii(text: string): string {
  let out = ''
  for (const ch of text) {
    const i = THAI_DIGITS.indexOf(ch)
    out += i >= 0 ? String(i) : ch
  }
  return out
}

export function phoneKind(digits: string): FoundPhone['kind'] | null {
  if (/^0[689]\d{8}$/.test(digits)) return 'mobile'
  if (/^0[2-7]\d{7}$/.test(digits)) return 'landline'
  return null
}

export function formatPhone(digits: string): string {
  if (digits.length === 10) return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`
  if (digits.startsWith('02')) return `02-${digits.slice(2, 5)}-${digits.slice(5)}`
  return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`
}

/** A run of digit groups joined by single separators: "085-883-6069", "+66 85 883 6069". */
const RUN = /\+?\d+(?:[ \-.]\d+)*/g
const WORD = /[A-Za-z0-9๐-๙•*#/_@]/

function layout(groups: string[]): string {
  return groups.map((g) => g.length).join(',')
}

/** The phone in `groups` (a window of one run), or null. `plus` = the run started with "+". */
function asPhone(groups: string[], plus: boolean): FoundPhone | null {
  const joined = groups.join('')
  let local: string
  let rest: string[]
  if (plus || (joined.startsWith('66') && (joined.length === 11 || joined.length === 10))) {
    if (!joined.startsWith('66')) return null
    // "66" alone or glued to the number: drop it and check the layout of what follows
    rest = groups[0] === '66' ? groups.slice(1) : [groups[0]!.slice(2), ...groups.slice(1)]
    if (!rest.length || rest.some((g) => !g)) return null
    const n = rest.join('')
    if (n.startsWith('0') || !INTL_LAYOUTS[n.length]?.includes(layout(rest))) return null
    local = `0${n}`
  } else {
    if (!joined.startsWith('0')) return null
    if (!LOCAL_LAYOUTS[joined.length]?.includes(layout(groups))) return null
    local = joined
  }
  const kind = phoneKind(local)
  return kind ? { digits: local, display: formatPhone(local), kind } : null
}

/** Phones written in a message, first seen first, without repeats (at most 3). */
export function detectPhones(text: string | null | undefined): FoundPhone[] {
  if (!text) return []
  const s = thaiToAscii(text)
  const found: FoundPhone[] = []
  for (const m of s.matchAll(RUN)) {
    const raw = m[0]
    const start = m.index ?? 0
    const before = s[start - 1] ?? ''
    const after = s[start + raw.length] ?? ''
    // glued to letters / other codes, or part of a decimal / thousands number ("1,216", "0.85")
    if (WORD.test(before) || WORD.test(after) || before === ',' || after === ',' || (after === '.' && /\d/.test(s[start + raw.length + 1] ?? ''))) {
      continue
    }
    const plus = raw.startsWith('+')
    const groups = (plus ? raw.slice(1) : raw).split(/[ \-.]/)
    // a phone can sit inside a longer run ("โอน 500 0858836069"): try every window, longest first
    let i = 0
    while (i < groups.length) {
      let hit: { phone: FoundPhone; end: number } | null = null
      for (let j = groups.length; j > i; j--) {
        const p = asPhone(groups.slice(i, j), plus && i === 0)
        if (p) {
          hit = { phone: p, end: j }
          break
        }
      }
      if (hit) {
        if (!found.some((f) => f.digits === hit!.phone.digits)) found.push(hit.phone)
        i = hit.end
      } else i += 1
      if (found.length >= MAX_PER_MESSAGE) return found
    }
  }
  return found
}

/** Last 4 digits (what a masked POS number "•••-•••-6069" still shows). */
export function last4(v: string | null | undefined): string {
  return (v ?? '').replace(/\D/g, '').slice(-4)
}
